import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import {
    ChevronRight,
    ExternalLink,
    FileText,
    Folder,
    FolderOpen,
    Plug,
} from '../../../../components/icon/solar-icons.js'
import { Skeleton } from '../../../../components/skeleton/skeleton.js'
import { DRIVE_FOLDER_MIME, type DriveFile, type DriveStatus } from '../../utils/portal-api.js'
import type { EmployeeDocument } from '../employee-profile/employee-profile-types.js'
import { DocumentList } from './document-list.js'
import './documents-view.css'

export interface Crumb {
    id: string
    name: string
}

interface DocumentsViewProps {
    lang: Language
    status: DriveStatus
    documents: EmployeeDocument[]
    trail: Crumb[]
    items: DriveFile[]
    loading: boolean
    error: string | null
    hasMore: boolean
    onConnect: () => void
    onDisconnect: () => void
    /** Browse a Drive-folder document inline; undefined when Drive isn't connected. */
    onOpenDocFolder?: (doc: EmployeeDocument) => void
    onOpenFolder: (file: DriveFile) => void
    onOpenFile: (file: DriveFile) => void
    onCrumb: (index: number) => void
    onLoadMore: () => void
}

const formatDate = (iso?: string): string => (iso ? iso.slice(0, 10) : '')

export const DocumentsView = ({
    lang,
    status,
    documents,
    trail,
    items,
    loading,
    error,
    hasMore,
    onConnect,
    onDisconnect,
    onOpenDocFolder,
    onOpenFolder,
    onOpenFile,
    onCrumb,
    onLoadMore,
}: DocumentsViewProps) => {
    const d = t.portal.documents
    const atRoot = trail.length === 0

    // ── Root: category-grouped document list (works without Drive) ──
    if (atRoot) {
        return (
            <>
                {status.connected ? (
                    <div className="portal-documents__toolbar">
                        <button
                            type="button"
                            className="portal-documents__disconnect"
                            onClick={onDisconnect}
                        >
                            {d.disconnect(lang)}
                        </button>
                    </div>
                ) : (
                    <div className="portal-documents__banner">
                        <Plug size={18} />
                        <div className="portal-documents__banner-body">
                            <span className="portal-documents__banner-text">
                                {status.needsReconnect
                                    ? d.reconnectDescription(lang)
                                    : d.browseHint(lang)}
                            </span>
                            <span className="portal-documents__banner-hint">
                                {d.unverifiedHint(lang)}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="portal-documents__connect"
                            onClick={onConnect}
                        >
                            {status.needsReconnect ? d.reconnect(lang) : d.connect(lang)}
                        </button>
                    </div>
                )}

                <DocumentList documents={documents} lang={lang} onOpenFolder={onOpenDocFolder} />
            </>
        )
    }

    // ── In a Drive folder: the browser (breadcrumb + contents) ──
    return (
        <>
            <div className="portal-documents__toolbar">
                <button
                    type="button"
                    className="portal-documents__disconnect"
                    onClick={onDisconnect}
                >
                    {d.disconnect(lang)}
                </button>
            </div>

            <nav className="portal-documents__crumbs" aria-label={t.common.breadcrumb(lang)}>
                <button
                    type="button"
                    className="portal-documents__crumb"
                    onClick={() => onCrumb(-1)}
                >
                    {d.home(lang)}
                </button>
                {trail.map((c, i) => (
                    <span key={c.id} className="portal-documents__crumb-group">
                        <ChevronRight size={14} className="portal-documents__crumb-sep" />
                        <button
                            type="button"
                            className="portal-documents__crumb"
                            disabled={i === trail.length - 1}
                            onClick={() => onCrumb(i)}
                        >
                            {c.name}
                        </button>
                    </span>
                ))}
            </nav>

            {loading && items.length === 0 ? (
                <div className="portal-documents__skeleton">
                    <Skeleton count={6} height={44} />
                </div>
            ) : error ? (
                <div className="portal-documents__empty">
                    <p>{error}</p>
                </div>
            ) : items.length === 0 ? (
                <div className="portal-documents__empty">
                    <FolderOpen size={48} />
                    <p>{d.emptyFolder(lang)}</p>
                </div>
            ) : (
                <>
                    <ul className="portal-documents__list">
                        {items.map((file) => {
                            const isFolder = file.mimeType === DRIVE_FOLDER_MIME
                            return (
                                <li key={file.id}>
                                    <button
                                        type="button"
                                        className="portal-documents__row"
                                        onClick={() =>
                                            isFolder ? onOpenFolder(file) : onOpenFile(file)
                                        }
                                    >
                                        {isFolder ? (
                                            <Folder
                                                size={18}
                                                className="portal-documents__row-icon"
                                            />
                                        ) : (
                                            <FileText
                                                size={18}
                                                className="portal-documents__row-icon"
                                            />
                                        )}
                                        <span className="portal-documents__row-name">
                                            {file.name}
                                        </span>
                                        <span className="portal-documents__row-meta">
                                            {formatDate(file.modifiedTime)}
                                        </span>
                                        {!isFolder && (
                                            <ExternalLink
                                                size={14}
                                                className="portal-documents__row-ext"
                                            />
                                        )}
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                    {hasMore && (
                        <button
                            type="button"
                            className="portal-documents__load-more"
                            disabled={loading}
                            onClick={onLoadMore}
                        >
                            {d.loadMore(lang)}
                        </button>
                    )}
                </>
            )}
        </>
    )
}
